import type { PollResponse } from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension/PollModal/api";
import { Button } from "@circleco/compass/components/Button";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { Typography } from "@circleco/compass/components/Typography";
import { differenceInMinutes, format } from "date-fns";
import { useEffect, useState } from "react";

import { t } from "@/i18n-js/instance";

import { getPollTimeRemaining } from "./getPollTimeRemaining";

export const PollFooter = ({
  poll,
  canUndo,
  undo,
}: {
  poll: PollResponse;
  canUndo: boolean;
  undo: () => void;
}) => {
  const canViewTotalResponses =
    poll.results && "total_responses" in poll.results;
  const [timeRemaining, setTimeRemaining] = useState<string | undefined>();

  useEffect(() => {
    if (poll.closes_at) {
      setTimeRemaining(getPollTimeRemaining(poll.closes_at));
      if (differenceInMinutes(new Date(poll.closes_at), new Date()) < 10) {
        const interval = setInterval(
          () => setTimeRemaining(getPollTimeRemaining(poll.closes_at)),
          1000,
        );
        return () => clearInterval(interval);
      }
    }
    return undefined;
  }, [poll.closes_at]);

  return (
    <div className="flex min-h-8 items-center gap-4" data-testid="poll-footer">
      {canViewTotalResponses && (
        <Typography
          color="secondary"
          component="span"
          data-testid="poll-total-votes"
          variant="label-sm"
        >
          {t("tiptap.extensions.poll.votes", {
            count: poll.results?.total_responses || 0,
          })}
        </Typography>
      )}
      {poll.closes_at && timeRemaining && (
        <Tooltip
          content={t("tiptap.extensions.poll.closes_at", {
            date: format(new Date(poll.closes_at), "MMMM do, yyyy, h:mm a"),
          })}
          data-testid="poll-closes-at"
        >
          <Typography color="secondary" component="span" variant="label-sm">
            {timeRemaining}
          </Typography>
        </Tooltip>
      )}
      {canUndo && (
        <Button onClick={undo} size="sm" type="button" variant="ghost">
          {t("tiptap.extensions.poll.undo")}
        </Button>
      )}
    </div>
  );
};
